import { ACESFilmicToneMapping, AmbientLight, Color, DirectionalLight, DirectionalLightHelper, FogExp2, Group, HemisphereLight, PMREMGenerator, PointLight, Quaternion, Scene, SRGBColorSpace, Vector3 } from "three";
import { SeaWeeds } from "./SeaWeeds";
import { Bubbles } from "./BubblesPoints";
import { CausticsWalls } from "./CausticsWalls";
import { RoomEnvironment } from "three/examples/jsm/Addons.js";
import { SceneProps } from "./SceneProps";
import { animate } from "@/lib/animate";
import { UnderwaterLightRays } from "./UnderwaterLightRays";
import { DEFAULT_UNDERWATER_VISUAL_SETTINGS } from "./VisualSettings";
import type { UnderwaterVisualSettings } from "./VisualSettings";
import { WaterGradientBackdrop } from "./WaterGradientBackdrop";
import { WaterSurface } from "./WaterSurface";
import { Seabed } from "./Seabed";
import { SuspendedParticles } from "./SuspendedParticles";
import { createEnvironmentDecorLayer } from "./EnvironmentDecor";
import { getSceneRevealStages } from "./SceneReveal";
import {
  createPollutionEffect,
  getPollutionLevelFromCounts,
} from "./PollutionEffects";
import { useSceneStore } from "@/lib/sceneStore";

const TOOL_INERTIA_POSITION_STRENGTH = 0.035;
const TOOL_INERTIA_MAX_OFFSET = 0.09;
const TOOL_INERTIA_POSITION_FOLLOW = 28;
const TOOL_INERTIA_ROTATION_FOLLOW = 32;
const TOOL_INERTIA_MAX_DT = 0.05;
const TOOL_INERTIA_MIN_DT = 0.001;
const TOOL_INTRO_VERTICAL_OFFSET = 1.15;
const SKIMMER_WEAPON_KEY = "skimmer";

const lerp = (from: number, to: number, progress: number) =>
  from + (to - from) * progress;

const getRevealedSettings = (
  settings: UnderwaterVisualSettings,
  revealProgress: number
): UnderwaterVisualSettings => {
  const stages = getSceneRevealStages(revealProgress);

  return {
    ...settings,
    toneExposure: settings.toneExposure * lerp(0.58, 1, stages.lighting),
    environmentIntensity:
      settings.environmentIntensity * lerp(0.08, 1, stages.lighting),
    ambientIntensity:
      settings.ambientIntensity * lerp(0.16, 1, stages.lighting),
    hemisphereIntensity:
      settings.hemisphereIntensity * lerp(0.12, 1, stages.lighting),
    keyIntensity: settings.keyIntensity * stages.lighting,
    fillIntensity: settings.fillIntensity * stages.lighting,
    rimIntensity: settings.rimIntensity * stages.lighting,
    shimmerIntensity: settings.shimmerIntensity * stages.lighting,
    cameraFillIntensity: settings.cameraFillIntensity * stages.gameplay,
    causticsOpacity: settings.causticsOpacity * stages.caustics,
    causticsIntensity: settings.causticsIntensity * stages.caustics,
    surfaceGlowIntensity: settings.surfaceGlowIntensity * stages.caustics,
    lightRaysOpacity: settings.lightRaysOpacity * stages.caustics,
    bubbleCount: Math.round(settings.bubbleCount * stages.particles),
    bubbleOpacity: settings.bubbleOpacity * stages.particles,
    particleCount: Math.round(settings.particleCount * stages.particles),
    particleOpacity: settings.particleOpacity * stages.particles,
    waterGradientOpacity: settings.waterGradientOpacity * stages.water,
    waterSurfaceOpacity: settings.waterSurfaceOpacity * stages.water,
    floorOpacity: settings.floorOpacity * stages.decor,
    seabedCaustics: settings.seabedCaustics * stages.caustics,
    seaweedCount: Math.round(settings.seaweedCount * stages.decor),
    seaweedTipLight: settings.seaweedTipLight * stages.lighting,
  };
};

export const addBasicObjects = ({
  scene,
  rootScene,
  renderer,
  camera,
  models,
  weaponModel,
  startPoint,
  visualSettings,
  environmentDecor: environmentDecorConfig,
  environmentDecorSource,
  pollutionEffect: pollutionEffectConfig,
  sceneReveal,
}: SceneProps) => {
  const roomSize = 5;
  const roomHeight = 7;
  const baseCameraHeight = 2;
  const getVisualSettings = () =>
    visualSettings?.get() ?? DEFAULT_UNDERWATER_VISUAL_SETTINGS;
  const getRevealProgress = () => sceneReveal?.getProgress() ?? 1;
  const getEnvironmentDecor = () =>
    environmentDecorSource?.get() ?? environmentDecorConfig;
  const initialSettings = getVisualSettings();
  const toolIntroDirection =
    weaponModel.key === SKIMMER_WEAPON_KEY ? 1 : -1;

  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = initialSettings.toneExposure;

  const ambientLight = new AmbientLight(
    new Color(initialSettings.ambientColor),
    initialSettings.ambientIntensity
  );
  scene.add(ambientLight);

  const hemisphereLight = new HemisphereLight(
    new Color(initialSettings.hemisphereSkyColor),
    new Color(initialSettings.hemisphereGroundColor),
    initialSettings.hemisphereIntensity
  );
  scene.add(hemisphereLight);

  const keyLight = new DirectionalLight(
    new Color(initialSettings.keyColor),
    initialSettings.keyIntensity
  );

  const fillLight = new DirectionalLight(
    new Color(initialSettings.fillColor),
    initialSettings.fillIntensity
  );
  fillLight.position.set(2.8, 1.6, -1.8);
  scene.add(fillLight);

  const rimLight = new DirectionalLight(
    new Color(initialSettings.rimColor),
    initialSettings.rimIntensity
  );
  rimLight.position.set(0, 2.4, -3.2);
  scene.add(rimLight);

  const shimmerLight = new PointLight(
    new Color(initialSettings.shimmerColor),
    initialSettings.shimmerIntensity,
    roomSize * 1.7,
    1.7
  );
  shimmerLight.position.set(-1.2, 1.1, -0.8);
  scene.add(shimmerLight);

  const pmremGenerator = new PMREMGenerator(renderer);
  const environmentScene = new RoomEnvironment();

  const bgTex = pmremGenerator.fromScene(environmentScene, 0.01).texture;
  const environmentTarget = rootScene ?? (scene instanceof Scene ? scene : undefined);
  const previousFog = environmentTarget?.fog ?? null;
  const depthFog = new FogExp2(
    new Color(initialSettings.depthFogColor),
    initialSettings.depthFogDensity
  );
  if (environmentTarget) {
    environmentTarget.environment = bgTex;
    environmentTarget.environmentIntensity = initialSettings.environmentIntensity;
    environmentTarget.fog =
      initialSettings.depthFogDensity > 0 ? depthFog : previousFog;
  }

  const waterGradient = new WaterGradientBackdrop(roomSize, roomHeight);
  waterGradient.position.set(0, roomHeight / 2 - baseCameraHeight, 0);
  waterGradient.setSettings(initialSettings);
  scene.add(waterGradient);

  const waterSurface = new WaterSurface(roomSize);
  waterSurface.setSettings(initialSettings);
  scene.add(waterSurface);

  // Визуальный объём воды
  const waterVolume = new CausticsWalls(roomSize, roomHeight, baseCameraHeight);
  waterVolume.position.set(0, roomHeight / 2 - baseCameraHeight, 0);
  waterVolume.setSettings(initialSettings);
  scene.add(waterVolume);

  const lightRays = new UnderwaterLightRays(roomSize, roomHeight, baseCameraHeight, initialSettings.lightRaysCount);
  lightRays.setSettings(initialSettings);
  scene.add(lightRays);

  const startCubePos = new Vector3(0, -roomHeight / 2 - baseCameraHeight, 0);
  const targetCubePos = new Vector3(0, roomHeight / 2 - baseCameraHeight, 0);

  animate({
    duration: 1000,
    onUpdate: (progress) => {
      waterVolume.position.lerpVectors(startCubePos, targetCubePos, progress);
    },
    onComplete: () => {
      waterVolume.position.copy(targetCubePos);
    }
  });

  // Дно
  const seabed = new Seabed(roomSize * 1.05, baseCameraHeight);
  seabed.setSettings(initialSettings, baseCameraHeight);
  scene.add(seabed);

  // Водоросли
  const seaweedMesh = new SeaWeeds(roomSize, roomHeight, baseCameraHeight, 650);
  seaweedMesh.position.set(0, -baseCameraHeight - 0.28, 0);
  seaweedMesh.setSettings(initialSettings);

  scene.add(seaweedMesh);

  const environmentDecor = createEnvironmentDecorLayer({
    models,
    scene,
    environmentDecor: getEnvironmentDecor(),
  });

  // Пузырьки
  const bubbleMesh = new Bubbles(roomSize, 1000);
  bubbleMesh.position.set(0, -roomHeight / 2 + baseCameraHeight, 0);
  bubbleMesh.setSettings(initialSettings);
  scene.add(bubbleMesh);

  // Мелкая взвесь в воде
  const suspendedParticles = new SuspendedParticles(roomSize, 1200);
  suspendedParticles.position.set(0, 0, 0);
  suspendedParticles.setSettings(initialSettings);
  scene.add(suspendedParticles);

  const pollutionEffect = createPollutionEffect(
    pollutionEffectConfig,
    roomSize,
    roomHeight
  );
  if (pollutionEffect) {
    scene.add(pollutionEffect.object);
  }

  // Группа объектов в руках
  const cameraHudGroup = new Group();
  cameraHudGroup.name = "camera-hud-group";

  const toolIntroGroup = new Group();
  toolIntroGroup.name = "tool-intro-group";

  const hudGroup = new Group();
  hudGroup.name = "tool-inertia-group";

  const cameraFillLight = new PointLight(
    new Color(initialSettings.cameraFillColor),
    initialSettings.cameraFillIntensity,
    3.2,
    1.6
  );
  cameraFillLight.position.set(0, 0.35, -0.85);
  cameraHudGroup.add(cameraFillLight);
  toolIntroGroup.add(hudGroup);
  cameraHudGroup.add(toolIntroGroup);

  scene.add(cameraHudGroup);

  let isHudInit = false;
  let hasToolInertiaState = false;
  let lastToolInertiaTime = performance.now() / 1000;
  const cameraWorldPosition = new Vector3();
  const cameraLocalPosition = new Vector3();
  const previousPlayerPosition = new Vector3();
  const cameraVelocity = new Vector3();
  const inertiaSceneOffset = new Vector3();
  const inertiaLocalOffset = new Vector3();
  const targetToolScenePosition = new Vector3();
  const smoothedToolScenePosition = new Vector3();
  const smoothedToolQuaternion = new Quaternion();
  const inverseCameraQuaternion = new Quaternion();
  const toolLocalQuaternion = new Quaternion();

  const getCameraPositionInContentSpace = () => {
    camera.getWorldPosition(cameraWorldPosition);
    cameraLocalPosition.copy(cameraWorldPosition);
    if (scene instanceof Group) {
      scene.worldToLocal(cameraLocalPosition);
    }
    return cameraLocalPosition;
  }
  
  animate({
    duration: 1000,
    onUpdate: (progress) => {
      cameraHudGroup.position.lerpVectors(startPoint, getCameraPositionInContentSpace(), progress);
    },
    onComplete: () => {
      isHudInit = true;
    }
  });

  const resetToolInertia = (playerPosition: Vector3, time: number) => {
    hasToolInertiaState = true;
    lastToolInertiaTime = time;
    previousPlayerPosition.copy(playerPosition);
    targetToolScenePosition.copy(playerPosition);
    smoothedToolScenePosition.copy(playerPosition);
    smoothedToolQuaternion.copy(camera.quaternion);
    hudGroup.position.set(0, 0, 0);
    hudGroup.quaternion.identity();
  };

  const updateToolInertia = (playerPosition: Vector3, time: number) => {
    if (!hasToolInertiaState) {
      resetToolInertia(playerPosition, time);
      return;
    }

    const elapsedTime = Math.max(
      TOOL_INERTIA_MIN_DT,
      time - lastToolInertiaTime
    );
    const dt = Math.min(TOOL_INERTIA_MAX_DT, elapsedTime);
    lastToolInertiaTime = time;

    cameraVelocity
      .copy(playerPosition)
      .sub(previousPlayerPosition)
      .divideScalar(elapsedTime);
    previousPlayerPosition.copy(playerPosition);

    inertiaSceneOffset
      .copy(cameraVelocity)
      .multiplyScalar(-TOOL_INERTIA_POSITION_STRENGTH)
      .clampLength(0, TOOL_INERTIA_MAX_OFFSET);
    targetToolScenePosition.copy(playerPosition).add(inertiaSceneOffset);

    const positionFollow = 1 - Math.exp(-dt * TOOL_INERTIA_POSITION_FOLLOW);
    smoothedToolScenePosition.lerp(targetToolScenePosition, positionFollow);

    inverseCameraQuaternion.copy(cameraHudGroup.quaternion).invert();
    inertiaLocalOffset
      .copy(smoothedToolScenePosition)
      .sub(playerPosition)
      .applyQuaternion(inverseCameraQuaternion);
    hudGroup.position.copy(inertiaLocalOffset);

    const rotationFollow = 1 - Math.exp(-dt * TOOL_INERTIA_ROTATION_FOLLOW);
    smoothedToolQuaternion.slerp(camera.quaternion, rotationFollow);
    toolLocalQuaternion
      .copy(inverseCameraQuaternion)
      .multiply(smoothedToolQuaternion);
    hudGroup.quaternion.copy(toolLocalQuaternion);
  };

  const ensureSceneLightingObjects = () => {
    if (ambientLight.parent !== scene) scene.add(ambientLight);
    if (hemisphereLight.parent !== scene) scene.add(hemisphereLight);
    if (keyLight.parent !== scene) scene.add(keyLight);
    if (fillLight.parent !== scene) scene.add(fillLight);
    if (rimLight.parent !== scene) scene.add(rimLight);
    if (shimmerLight.parent !== scene) scene.add(shimmerLight);
    if (cameraHudGroup.parent !== scene) scene.add(cameraHudGroup);

    if (environmentTarget && environmentTarget.environment !== bgTex) {
      environmentTarget.environment = bgTex;
    }
  };

  return {
    cameraHudGroup,
    hudGroup,
    onExit: () => {
      environmentDecor.dispose();
      if (pollutionEffect) {
        scene.remove(pollutionEffect.object);
        pollutionEffect.dispose();
      }
      if (environmentTarget) {
        environmentTarget.fog = previousFog;
      }
    },
    onUpdate: () => {
      const time = performance.now() / 1000;
      const revealProgress = getRevealProgress();
      const revealStages = getSceneRevealStages(revealProgress);
      const settings = getRevealedSettings(getVisualSettings(), revealProgress);
      const playerPosition = getCameraPositionInContentSpace();
      const { collectedGarbageCount, totalGarbageCount } =
        useSceneStore.getState();
      const pollutionLevel = getPollutionLevelFromCounts(
        collectedGarbageCount,
        totalGarbageCount
      );

      ensureSceneLightingObjects();
      renderer.toneMappingExposure = settings.toneExposure;
      ambientLight.color.set(settings.ambientColor);
      ambientLight.intensity = settings.ambientIntensity;
      hemisphereLight.color.set(settings.hemisphereSkyColor);
      hemisphereLight.groundColor.set(settings.hemisphereGroundColor);
      hemisphereLight.intensity = settings.hemisphereIntensity;
      keyLight.color.set(settings.keyColor);
      fillLight.color.set(settings.fillColor);
      rimLight.color.set(settings.rimColor);
      rimLight.intensity = settings.rimIntensity;
      shimmerLight.color.set(settings.shimmerColor);
      cameraFillLight.color.set(settings.cameraFillColor);
      cameraFillLight.intensity = settings.cameraFillIntensity;
      if (environmentTarget) {
        environmentTarget.environmentIntensity = settings.environmentIntensity;
        depthFog.color.set(settings.depthFogColor);
        depthFog.density = settings.depthFogDensity;
        environmentTarget.fog =
          settings.depthFogDensity > 0 ? depthFog : previousFog;
      }
      waterGradient.setSettings(settings);
      waterSurface.setSettings(settings);
      environmentDecor.setItems(getEnvironmentDecor());
      environmentDecor.setRevealProgress(revealStages.decor);
      seabed.setSettings(settings, baseCameraHeight);
      waterVolume.setSettings(settings);
      lightRays.setSettings(settings);
      waterVolume.update(time);
      waterSurface.update(time);
      lightRays.update(time, settings.lightRaysSpeed, camera.getWorldDirection(new Vector3()));
      seabed.update(time);
      seaweedMesh.setSettings(settings);
      seaweedMesh.update(time);
      bubbleMesh.setSettings(settings);
      bubbleMesh.update(time, playerPosition);
      suspendedParticles.setSettings(settings);
      suspendedParticles.update(time);
      if (pollutionEffect) {
        pollutionEffect.setPollutionLevel(pollutionLevel);
        pollutionEffect.update(time);
      }
      cameraHudGroup.visible = revealStages.gameplay > 0.01;
      toolIntroGroup.position.y =
        toolIntroDirection *
        TOOL_INTRO_VERTICAL_OFFSET *
        (1 - revealStages.gameplay);

      keyLight.intensity =
        settings.keyIntensity + Math.sin(time * 0.55) * 0.15 * revealStages.lighting;
      fillLight.intensity =
        settings.fillIntensity + Math.sin(time * 0.7 + 1.8) * 0.1 * revealStages.lighting;
      shimmerLight.intensity =
        settings.shimmerIntensity + Math.sin(time * 1.25) * 0.18 * revealStages.lighting;
      shimmerLight.position.set(
        Math.sin(time * 0.33) * 1.7,
        1.0 + Math.sin(time * 0.47) * 0.25,
        Math.cos(time * 0.29) * 1.7
      );

      if (isHudInit) {
        cameraHudGroup.quaternion.copy(camera.quaternion);
        cameraHudGroup.position.copy(playerPosition);
        updateToolInertia(playerPosition, time);
      }
    }
  }
}

export type BasicSceneObjects = ReturnType<typeof addBasicObjects>;
